import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { BillingDetails } from "@/components/billing/BillingDetails";
import { Invoices } from "@/components/billing/Invoices";
import { PaymentIssue, PortalButton } from "@/components/billing/Portal";
import { ChangePlan, PlanPicker } from "@/components/billing/PlanPicker";

const eur = (n) => n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const STATE = { trialing: ["Essai gratuit", "bg-blue-100 text-blue-800"], active: ["Abonnement actif", "bg-green-100 text-green-800"], expired: ["Essai terminé", "bg-red-100 text-red-700"] };

const Stat = ({ label, value, id }) => (
  <div className="rounded-lg border border-slate-200 p-5">
    <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
    <p className="text-2xl font-semibold mt-1" data-testid={id}>{value}</p>
  </div>
);

export default function Billing() {
  const { billing, reloadBilling } = useOutletContext();
  const [busy, setBusy] = useState(false);
  const [seats, setSeats] = useState("");
  const [pick, setPick] = useState(null);
  if (!billing) return <div className="p-10 text-slate-500">Chargement…</div>;
  const [label, tone] = billing.payment_issue ? ["Paiement refusé", "bg-red-100 text-red-700"] : STATE[billing.state];
  const seatsCount = Math.max(1, billing.seats_used);
  const chosen = pick?.plan_id || billing.plan?.plan_id;
  const unit = pick?.price_eur ?? billing.price_eur;

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin, plan_id: chosen });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  const updateSeats = async () => {
    setBusy(true);
    try {
      await api.post("/billing/seats", { seats: Number(seats) });
      toast.success("Licences mises à jour");
      setSeats("");
      reloadBilling();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto px-6 lg:px-10 py-8 max-w-4xl">
      <PageHeader kicker="ABONNEMENT" title="Licences techniciens">
        <span data-testid="billing-state" className={`px-3 py-1 rounded-full text-sm font-semibold ${tone}`}>{label}</span>
      </PageHeader>
      {billing.payment_issue && <PaymentIssue billing={billing} />}
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <Stat label="Comptes utilisés" value={billing.seats_used} id="billing-seats-used" />
        <Stat label="Prix par technicien" value={`${eur(billing.state === "active" ? billing.price_eur : unit)} HT/mois`} id="billing-price" />
        <Stat label={billing.state === "active" ? "Licences payées" : "Total mensuel"} value={billing.state === "active" ? billing.seats_paid : eur(unit * seatsCount)} id="billing-total" />
      </div>
      {billing.state !== "active" ? (
        <div className="rounded-lg border border-slate-200 p-6">
          <p className="text-slate-700 mb-1">
            {billing.state === "trialing" ? `Votre essai gratuit se termine le ${fmtDate(billing.trial_ends_at)}.` : billing.has_subscription ? "Votre abonnement est arrêté (résilié ou impayé) : les connexions aux machines sont bloquées." : "Votre essai est terminé : les connexions aux machines sont bloquées."}
          </p>
          <p className="text-sm text-slate-500 mb-5">Chaque compte (administrateur et techniciens) compte pour une licence. Renouvellement automatique chaque mois, résiliable à tout moment.</p>
          <PlanPicker value={chosen} onChange={(id, p) => setPick(p)} />
          <Button data-testid="subscribe-btn" onClick={subscribe} disabled={busy} className="h-11 px-6">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} S'abonner — {seatsCount} licence{seatsCount > 1 ? "s" : ""} × {eur(unit)} HT/mois
          </Button>
          <p className="text-xs text-slate-500 mt-3">Code promo : saisissez-le sur la page de paiement. Mode test : carte 4242 4242 4242 4242, date future, CVC au choix.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 p-6 space-y-4">
          {billing.cancel_at_period_end ? (
            <p className="text-amber-700" data-testid="billing-cancel-notice">Résiliation demandée : l'abonnement s'arrêtera le {fmtDate(billing.paid_until)}.</p>
          ) : billing.has_subscription && !billing.payment_issue && (
            <p className="flex items-center gap-2 text-green-700"><CheckCircle2 className="h-5 w-5" /> Abonnement actif — prochain renouvellement vers le {fmtDate(billing.paid_until)}.</p>
          )}
          {billing.has_subscription && !billing.cancel_at_period_end && <ChangePlan billing={billing} onDone={reloadBilling} />}
          {billing.has_customer && (
            <div className="flex flex-wrap items-center gap-3">
              <PortalButton variant="outline" />
              <span className="text-xs text-slate-500">Carte bancaire, factures, résiliation</span>
            </div>
          )}
          <div>
            <p className="text-sm text-slate-600 mb-2">Changer le nombre de licences (au moins {billing.seats_used}) :</p>
            <div className="flex gap-2 max-w-sm">
              <Input data-testid="seats-input" type="number" min={billing.seats_used} placeholder={String(billing.seats_paid)} value={seats} onChange={(e) => setSeats(e.target.value)} />
              <Button data-testid="update-seats-btn" onClick={updateSeats} disabled={!seats || busy}>Mettre à jour</Button>
            </div>
            <p className="text-xs text-slate-500 mt-2">La différence est calculée au prorata sur la prochaine facture.</p>
          </div>
        </div>
      )}
      <div className="space-y-6 mt-8">
        <BillingDetails />
        <Invoices />
      </div>
    </div>
  );
}
